import type { TargetChip } from '@/forms/shared/targetChip';

export interface LocationTargetGroup {
  locationId: number;
  // Human-readable labels for every chip that resolved to this location, e.g. "Machine 101".
  sources: string[];
}

export interface ResolvedLocationTargets {
  locationIds: number[];
  // Groups with more than one source - i.e. two or more chips (usually machines) that
  // collapsed into the same location. Surfaced to the user so the dedupe is never silent.
  duplicateGroups: LocationTargetGroup[];
  // Machine chips that resolved to a real machine but that machine has no location (e.g. a
  // machine pulled from service) - can't back a location-scoped ticket, so they're excluded
  // from locationIds rather than silently dropped.
  unresolvable: string[];
}

const sourceLabel = (target: TargetChip) =>
  target.type === 'atm' ? `Machine ${target.value}` : `Location ${target.value}`;

/**
 * Service tickets are location-scoped, not machine-scoped (Boro's bulk endpoint creates one
 * ticket per location). A machine chip resolves to the location it's installed at; if two
 * machine chips (or a machine and a location chip) resolve to the same location, that location
 * must only be requested once - otherwise the same location would get duplicate tickets.
 */
export const resolveLocationTargets = (targets: TargetChip[]): ResolvedLocationTargets => {
  const sourcesByLocation = new Map<number, string[]>();
  const unresolvable: string[] = [];

  for (const target of targets) {
    if (target.status !== 'valid') continue;

    if (target.type === 'atm' && !target.resolvedLocationId) {
      unresolvable.push(sourceLabel(target));
      continue;
    }

    const locationId =
      target.type === 'location' ? Number.parseInt(target.value, 10) : target.resolvedLocationId;

    if (locationId === null || locationId === undefined || Number.isNaN(locationId)) continue;

    const sources = sourcesByLocation.get(locationId) ?? [];
    sources.push(sourceLabel(target));
    sourcesByLocation.set(locationId, sources);
  }

  const duplicateGroups: LocationTargetGroup[] = [];
  for (const [locationId, sources] of sourcesByLocation) {
    if (sources.length > 1) {
      duplicateGroups.push({ locationId, sources });
    }
  }

  return {
    locationIds: [...sourcesByLocation.keys()],
    duplicateGroups,
    unresolvable,
  };
};
